import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ChevronLeft, Play, Upload, FileText, AlertCircle, Loader2, Download, XCircle, Sparkles } from 'lucide-react';
import { Button, InputField, TabSwitcher } from 'equal-ds-ui';
import { analyticsOnboardingAPI } from '../../../services/analytics-onboarding-api.service';
import { WORKFLOW_IDS } from '../../../types/analytics-onboarding';
import type { ClientAnalyticsConfig, ConfiguredJourneyInfo } from '../../../types/analytics-onboarding';

interface TestAnalyticsViewProps {
  clientConfig: ClientAnalyticsConfig;
  onBack: () => void;
  onConfigure: () => void;
  onError: (err: unknown, context?: Record<string, any>) => void;
}

/* ── Copy button ── */
const CopyBtn: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: copied ? '#22c55e' : '#64748b', fontSize: 11, transition: 'color 0.2s' }}>
      {copied ? (
        <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg> Copied!</>
      ) : (
        <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg> Copy</>
      )}
    </button>
  );
};

/* ── Curl + Response (Consent tab) ── */
const CurlPanel: React.FC<{
  title: string; curl: string; response: any; error: string;
}> = ({ title, curl, response, error }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ borderRadius: 12, overflow: 'hidden', backgroundColor: '#1e293b', border: '1px solid #334155' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #334155' }}>
        <span style={{ fontSize: 12, color: '#e2e8f0' }}><b>{title} - cURL Request</b></span>
      </div>
      <pre className="curl-scroll" style={{ padding: '16px', margin: 0, fontSize: 12, color: '#e2e8f0', overflowX: 'auto', overflowY: 'auto', maxHeight: 280, fontFamily: "'SF Mono', ui-monospace, Menlo, monospace", lineHeight: 1.7, whiteSpace: 'pre' }}>
        {curl.split('\n').map((line, i) => (
          <div key={i} style={{ display: 'flex', gap: 16, whiteSpace: 'nowrap' }}>
            <span style={{ color: '#475569', userSelect: 'none', minWidth: 20, textAlign: 'right' }}>{i + 1}</span>
            <span>{line}</span>
          </div>
        ))}
      </pre>
      <div style={{ padding: '10px 16px', borderTop: '1px solid #334155' }}>
        <CopyBtn text={curl} />
      </div>
    </div>

    <div style={{ height: 16 }} />

    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0', backgroundColor: '#fff' }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9' }}>
        <span style={{ fontSize: 12, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em' }}><b>{title} - Response</b></span>
      </div>
      {response || error ? (
        (() => {
          const isExcel = response?.response_type === 'excel' && response?.file_base64;
          const isErr = !!error || response?.status === 'FAILED' || response?.status === false || response?.errorCode || response?.errorMsg || (response?.http_status && response.http_status >= 400);
          const downloadExcel = () => {
            if (!response?.file_base64) return;
            const byteChars = atob(response.file_base64);
            const byteArr = new Uint8Array(byteChars.length);
            for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
            const blob = new Blob([byteArr], { type: response.content_type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob); const a = document.createElement('a');
            a.href = url; a.download = response.file_name || `analytics-${Date.now()}.xlsx`; a.click(); URL.revokeObjectURL(url);
          };
          return (
            <div>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, border: '1px solid', borderColor: isErr ? '#fca5a5' : '#bbf7d0', color: isErr ? '#dc2626' : '#16a34a', backgroundColor: isErr ? '#fef2f2' : '#f0fdf4' }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: isErr ? '#dc2626' : '#16a34a' }} />
                  <b>{error ? 'Error' : 'Success'}</b>
                </span>
                {!error && response && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    {isExcel && (
                      <button onClick={downloadExcel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 12, color: '#fff', border: 'none', borderRadius: 6, backgroundColor: '#059669', cursor: 'pointer' }}>
                        <Download style={{ width: 12, height: 12 }} /> Download Excel
                      </button>
                    )}
                    <button onClick={() => {
                      const displayData = isExcel ? { ...response, file_base64: `<${Math.round((response.file_base64?.length || 0) / 1024)}KB base64 omitted>` } : response;
                      const blob = new Blob([JSON.stringify(displayData, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob); const a = document.createElement('a');
                      a.href = url; a.download = `analytics-response-${Date.now()}.json`; a.click(); URL.revokeObjectURL(url);
                    }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 12, color: '#fff', border: 'none', borderRadius: 6, backgroundColor: '#2563eb', cursor: 'pointer' }}>
                      <Download style={{ width: 12, height: 12 }} /> {isExcel ? 'Download Meta' : 'Download JSON'}
                    </button>
                  </div>
                )}
              </div>
              {isExcel && !error ? (
                <div style={{ padding: '28px 16px', textAlign: 'center', backgroundColor: '#f0fdf4' }}>
                  <div style={{ display: 'inline-flex', width: 48, height: 48, borderRadius: '50%', backgroundColor: '#dcfce7', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                    <FileText style={{ width: 22, height: 22, color: '#16a34a' }} />
                  </div>
                  <div style={{ fontSize: 14, color: '#166534' }}><b>Excel report ready</b></div>
                  <div style={{ fontSize: 12, color: '#15803d', marginTop: 4 }}>Click "Download Excel" above to save the .xlsx file.</div>
                </div>
              ) : (
                <pre style={{ padding: '16px', margin: 0, fontSize: 12, color: error ? '#dc2626' : '#0f172a', backgroundColor: '#f8fafc', maxHeight: 420, overflow: 'auto', fontFamily: "'SF Mono', ui-monospace, Menlo, monospace", lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {error ? error : JSON.stringify(response, null, 2)}
                </pre>
              )}
            </div>
          );
        })()
      ) : (
        <div style={{ padding: '36px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No response yet. Click "Generate Analytics" to see the result.</div>
      )}
    </div>
  </div>
);


/* ── View Data modal ── */
const DataModal: React.FC<{ open: boolean; data: any; onClose: () => void }> = ({ open, data, onClose }) => {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9998 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(900px, 92vw)', maxHeight: '85vh', backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', zIndex: 9999, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, color: '#0f172a' }}><b>Analytics Response</b></div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#64748b' }}>
            <XCircle style={{ width: 20, height: 20 }} />
          </button>
        </div>
        <pre style={{ flex: 1, padding: '20px 24px', margin: 0, overflow: 'auto', fontSize: 12, color: '#0f172a', fontFamily: "'SF Mono', ui-monospace, Menlo, monospace", lineHeight: 1.6, backgroundColor: '#f8fafc' }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    </>
  );
};

/* ── Active Journey Selector ── */
const ActiveJourneySelector: React.FC<{
  label: string;
  activeJourneyId: string;
  availableJourneys: any[];
  activatingJourney: string | null;
  showPicker: boolean;
  onTogglePicker: () => void;
  onActivate: (id: string) => void;
  onConfigure: () => void;
}> = ({ label, activeJourneyId, availableJourneys, activatingJourney, showPicker, onTogglePicker, onActivate, onConfigure }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Journey</div>

    {activeJourneyId ? (
      <div style={{ borderRadius: 10, border: '1px solid #bbf7d0', overflow: 'hidden', backgroundColor: '#f0fdf4' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#16a34a', flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#15803d', fontFamily: "'SF Mono', ui-monospace, monospace" }}>{activeJourneyId}</span>
          <span style={{ fontSize: 11, color: '#15803d', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: 20, padding: '1px 8px', fontWeight: 700, whiteSpace: 'nowrap' }}>✓ Active</span>
          <div className="equal-ds-root">
            <Button variant="secondary" size="sm" onClick={onTogglePicker}>{showPicker ? 'Cancel' : 'Change'}</Button>
          </div>
        </div>
      </div>
    ) : (
      <div style={{ borderRadius: 10, border: '1px solid #fde68a', backgroundColor: '#fffbeb', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle style={{ width: 15, height: 15, color: '#d97706', flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: '#92400e' }}>No active journey for <b>{label}</b></span>
        </div>
        <div className="equal-ds-root">
          {availableJourneys.length > 0
            ? <Button variant="secondary" size="sm" onClick={onTogglePicker}>{showPicker ? 'Cancel' : 'Set Active'}</Button>
            : <Button variant="secondary" size="sm" onClick={onConfigure}>Configure →</Button>}
        </div>
      </div>
    )}

    {showPicker && availableJourneys.length > 0 && (
      <div style={{ marginTop: 6, borderRadius: 10, border: '1px solid #e2e8f0', backgroundColor: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ padding: '10px 16px 6px', borderBottom: '1px solid #f1f5f9' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Select a journey to activate</span>
        </div>
        {availableJourneys.map((j, i) => {
          const isActivating = activatingJourney === j.journey_id;
          const isCurrent = j.journey_id === activeJourneyId;
          return (
            <div key={j.journey_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: i < availableJourneys.length - 1 ? '1px solid #f8fafc' : 'none', backgroundColor: isCurrent ? '#f0fdf4' : '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: isCurrent ? '#16a34a' : '#e2e8f0', flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: isCurrent ? 600 : 400, color: isCurrent ? '#15803d' : '#0f172a', fontFamily: "'SF Mono', ui-monospace, monospace" }}>{j.journey_id}</span>
              </div>
              <div className="equal-ds-root">
                {isCurrent
                  ? <span style={{ fontSize: 11, color: '#15803d', fontWeight: 700 }}>✓ Active</span>
                  : <Button variant="primary" size="sm" onClick={() => onActivate(j.journey_id)} disabled={!!activatingJourney}>
                      {isActivating ? <><Loader2 style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} /> Activating...</> : 'Make Active'}
                    </Button>
                }
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const TEST_STATE_KEY = 'analytics_test_state';
const loadTestState = (): any => {
  try { const r = sessionStorage.getItem(TEST_STATE_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
};

export const TestAnalyticsView: React.FC<TestAnalyticsViewProps> = ({ clientConfig, onBack, onConfigure, onError: _onError }) => {
  const saved = loadTestState();

  const [activeTab, setActiveTab] = useState<'consent' | 'pdf'>(saved?.activeTab || 'consent');

  // Journeys (shared)
  const [journeys, setJourneys] = useState<ConfiguredJourneyInfo[]>([]);
  const [activeJourneys, setActiveJourneys] = useState<Record<string, string | null>>({});
  const [activatingJourney, setActivatingJourney] = useState<string | null>(null);

  // Filter journeys by workflow type
  const consentJourneys = useMemo(() => journeys.filter(j => j.journey_workflow_config?.workflow_id === WORKFLOW_IDS.CONSENT), [journeys]);
  const pdfJourneys = useMemo(() => journeys.filter(j => j.journey_workflow_config?.workflow_id === WORKFLOW_IDS.PDF), [journeys]);
  // Split by Excel/JSON
  const consentJsonJourneys = useMemo(() => consentJourneys.filter(j => !(j.journey_workflow_config?.workflow_configuration as any)?.report_configuration?.excel_template), [consentJourneys]);
  const consentExcelJourneys = useMemo(() => consentJourneys.filter(j => !!(j.journey_workflow_config?.workflow_configuration as any)?.report_configuration?.excel_template), [consentJourneys]);

  useEffect(() => {
    const fetchAll = async () => {
      const [journeyData, activeData] = await Promise.all([
        clientConfig.exchange_application_id
          ? analyticsOnboardingAPI.getConfiguredJourneys(clientConfig.exchange_application_id).catch(() => null)
          : Promise.resolve(null),
        analyticsOnboardingAPI.getActiveJourneys().catch(() => ({})),
      ]);
      if (journeyData) {
        const loaded: ConfiguredJourneyInfo[] = journeyData?.configured_journeys || [];
        setJourneys(loaded);

        // Auto-select the format tab that has journeys so user doesn't land on an empty tab
        const consentLoaded = loaded.filter(j => j.journey_workflow_config?.workflow_id === WORKFLOW_IDS.CONSENT);
        const hasConsentJson = consentLoaded.some(j => !(j.journey_workflow_config?.workflow_configuration as any)?.report_configuration?.excel_template);
        const hasConsentExcel = consentLoaded.some(j => !!(j.journey_workflow_config?.workflow_configuration as any)?.report_configuration?.excel_template);
        if (!hasConsentJson && hasConsentExcel) setConsentFormat('excel');
        else if (hasConsentJson) setConsentFormat('json');

        const pdfLoaded = loaded.filter(j => j.journey_workflow_config?.workflow_id === WORKFLOW_IDS.PDF);
        const hasPdfJson = pdfLoaded.some(j => !(j.journey_workflow_config?.workflow_configuration as any)?.report_configuration?.excel_template);
        const hasPdfExcel = pdfLoaded.some(j => !!(j.journey_workflow_config?.workflow_configuration as any)?.report_configuration?.excel_template);
        if (!hasPdfJson && hasPdfExcel) setPdfFormat('excel');
        else if (hasPdfJson) setPdfFormat('json');
      }
      setActiveJourneys(activeData || {});
    };
    fetchAll();
  }, [clientConfig.exchange_application_id]);

  // Activate a journey inline and update local state
  const activateJourney = useCallback(async (journeyId: string, type: 'analytics-json' | 'analytics-excel' | 'pdf-json' | 'pdf-excel') => {
    setActivatingJourney(journeyId);
    try {
      await analyticsOnboardingAPI.enableJourney(journeyId, type);
      setActiveJourneys(prev => ({ ...prev, [type]: journeyId }));
    } catch (err) {
      _onError(err, { component: 'TestAnalyticsView', action: 'activateJourney', journeyId, type });
    } finally {
      setActivatingJourney(null);
    }
  }, [_onError]);

  // Persist tab
  useEffect(() => {
    try { sessionStorage.setItem(TEST_STATE_KEY, JSON.stringify({ activeTab })); } catch {}
  }, [activeTab]);

  /* ── Consent tab state ── */
  const [consentId, setConsentId] = useState('');
  const [consentFormat, setConsentFormat] = useState<'json' | 'excel'>('json');
  const [consentRunning, setConsentRunning] = useState(false);
  const [consentResponse, setConsentResponse] = useState<any>(null);
  const [consentError, setConsentError] = useState('');
  const [showConsentPicker, setShowConsentPicker] = useState(false);

  // Active journey for current format — read from DB
  const consentActiveType = consentFormat === 'excel' ? 'analytics-excel' : 'analytics-json';
  const consentJourney = activeJourneys[consentActiveType] || '';
  const activeConsentJourneys = consentFormat === 'excel' ? consentExcelJourneys : consentJsonJourneys;


  const consentCurl = useMemo(() => {
    const fiu = clientConfig?.fiu_creds || {} as any;
    const orgId         = fiu.organisationId || '<organisationId>';
    const clientId      = fiu.client_id      || '<client_id>';
    const clientSecret  = fiu.client_secret  || '<client_secret>';
    const appIdentifier = fiu.appIdentifier  || '<appIdentifier>';
    const baseUrl = (clientConfig?.finpro_base_url || 'https://<finpro-server>').replace(/\/$/, '');
    const body = JSON.stringify({ consentID: consentId || '<consent_id>', journeyID: consentJourney || '<journey_id>' });
    return `curl --request POST \\\n  --url ${baseUrl}/analytics/${consentFormat} \\\n  --header 'Content-Type: application/json' \\\n  --header 'organisationId: ${orgId}' \\\n  --header 'client_id: ${clientId}' \\\n  --header 'client_secret: ${clientSecret}' \\\n  --header 'appIdentifier: ${appIdentifier}' \\\n  --data '${body}'`;
  }, [consentId, consentJourney, consentFormat, clientConfig]);

  const runConsent = useCallback(async () => {
    if (!consentId.trim() || !consentJourney) return;
    setConsentRunning(true); setConsentResponse(null); setConsentError('');
    try {
      const data = await analyticsOnboardingAPI.runConsentAnalytics(consentId.trim(), consentJourney, consentFormat);
      setConsentResponse(data);
    } catch (err: any) {
      setConsentError(err?.response?.data?.message || err?.message || 'Request failed');
    } finally { setConsentRunning(false); }
  }, [consentId, consentJourney, consentFormat]);

  /* ── PDF tab state ── */
  const [pdfFormat, setPdfFormat] = useState<'json' | 'excel'>('json');
  const [showPdfPicker, setShowPdfPicker] = useState(false);
  const [pdfFileName, setPdfFileName] = useState('');
  const [pdfFileBase64, setPdfFileBase64] = useState('');
  const [pdfRefToken, setPdfRefToken] = useState('');
  const [pdfPushRunning, setPdfPushRunning] = useState(false);
  const [pdfPushResponse, setPdfPushResponse] = useState<any>(null);
  const [pdfPushError, setPdfPushError] = useState('');
  const [pdfPullRunning, setPdfPullRunning] = useState(false);
  const [pdfPullResponse, setPdfPullResponse] = useState<any>(null);
  const [pdfPullError, setPdfPullError] = useState('');
  const [pdfViewOpen, setPdfViewOpen] = useState(false);

  // Split PDF journeys into JSON (no excel_template) and Excel (has excel_template)
  const pdfJsonJourneys = useMemo(() => pdfJourneys.filter(j => !(j.journey_workflow_config?.workflow_configuration as any)?.report_configuration?.excel_template), [pdfJourneys]);
  const pdfExcelJourneys = useMemo(() => pdfJourneys.filter(j => !!(j.journey_workflow_config?.workflow_configuration as any)?.report_configuration?.excel_template), [pdfJourneys]);
  const activePdfJourneys = pdfFormat === 'excel' ? pdfExcelJourneys : pdfJsonJourneys;

  // Active journey for current PDF format — read from DB
  const pdfActiveType = pdfFormat === 'excel' ? 'pdf-excel' : 'pdf-json';
  const pdfJourney = activeJourneys[pdfActiveType] || '';


  const handlePdfFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      setPdfFileBase64(base64);
      setPdfFileName(file.name);
      setPdfRefToken(''); setPdfPushResponse(null); setPdfPullResponse(null);
      setPdfPushError(''); setPdfPullError('');
    };
    reader.readAsDataURL(file);
  }, []);

  const pdfPushCurl = useMemo(() => {
    const fiu = clientConfig?.fiu_creds || {} as any;
    const orgId = fiu.organisationId || '<organisationId>';
    const cid = fiu.client_id || '<client_id>';
    const csecret = fiu.client_secret || '<client_secret>';
    const appId = fiu.appIdentifier || '<appIdentifier>';
    const baseUrl = (clientConfig?.finpro_base_url || 'https://<finpro-server>').replace(/\/$/, '');
    const body = JSON.stringify({
      customer_name: "customer name", mobile_number: "0000000000",
      bank_account_number: "0000000000", loan_account_number: "0000000000",
      analytics_type_identifier: pdfJourney || '<journey_id>',
      fileData: [{ file: pdfFileName || '<filename>.pdf', password: "nopassword", file_blob: pdfFileBase64 || '<base64_pdf_content>' }]
    }, null, 2);
    return `curl --request POST \\\n  --url ${baseUrl}/analytics/pdf/push \\\n  --header 'Content-Type: application/json' \\\n  --header 'organisationId: ${orgId}' \\\n  --header 'client_id: ${cid}' \\\n  --header 'client_secret: ${csecret}' \\\n  --header 'appIdentifier: ${appId}' \\\n  --data '${body}'`;
  }, [pdfJourney, pdfFileName, pdfFileBase64, clientConfig]);

  const pdfPullCurl = useMemo(() => {
    const fiu = clientConfig?.fiu_creds || {} as any;
    const orgId = fiu.organisationId || '<organisationId>';
    const cid = fiu.client_id || '<client_id>';
    const csecret = fiu.client_secret || '<client_secret>';
    const appId = fiu.appIdentifier || '<appIdentifier>';
    const baseUrl = (clientConfig?.finpro_base_url || 'https://<finpro-server>').replace(/\/$/, '');
    const body = JSON.stringify({ ref_token: pdfRefToken || '<ref_token>' });
    return `curl --request POST \\\n  --url ${baseUrl}/analytics/pdf/pull/${pdfFormat} \\\n  --header 'Content-Type: application/json' \\\n  --header 'organisationId: ${orgId}' \\\n  --header 'client_id: ${cid}' \\\n  --header 'client_secret: ${csecret}' \\\n  --header 'appIdentifier: ${appId}' \\\n  --data '${body}'`;
  }, [pdfRefToken, pdfFormat, clientConfig]);

  const runPdfPush = useCallback(async () => {
    if (!pdfFileBase64 || !pdfJourney) return;
    setPdfPushRunning(true); setPdfPushResponse(null); setPdfPushError('');
    setPdfRefToken(''); setPdfPullResponse(null); setPdfPullError('');
    try {
      const data = await analyticsOnboardingAPI.runPdfPush(pdfJourney, pdfFileName, pdfFileBase64, pdfFormat);
      setPdfPushResponse(data);
      const refToken = data?.ref_token || data?.data?.ref_token;
      if (refToken) setPdfRefToken(refToken);
    } catch (err: any) {
      setPdfPushError(err?.response?.data?.message || err?.message || 'Push failed');
    } finally { setPdfPushRunning(false); }
  }, [pdfJourney, pdfFileName, pdfFileBase64, pdfFormat]);

  const runPdfPull = useCallback(async () => {
    if (!pdfRefToken) return;
    setPdfPullRunning(true); setPdfPullResponse(null); setPdfPullError('');
    try {
      const data = await analyticsOnboardingAPI.runPdfPull(pdfRefToken, pdfFormat);
      setPdfPullResponse(data);
    } catch (err: any) {
      setPdfPullError(err?.response?.data?.message || err?.message || 'Pull failed');
    } finally { setPdfPullRunning(false); }
  }, [pdfRefToken, pdfFormat]);

  // keep legacy pdfData for DataModal
  const pdfData = pdfPullResponse ?? pdfPushResponse;



  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 0 60px' }}>
      <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', marginBottom: 28 }}>
        <ChevronLeft style={{ width: 14, height: 14 }} /> Back to Analytics
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Sparkles style={{ width: 20, height: 20, color: '#2563eb' }} />
        </div>
        <div style={{ fontSize: 22, color: '#0f172a' }}><b>Test Analytics</b></div>
      </div>
      <div style={{ fontSize: 14, color: '#64748b', marginBottom: 28 }}>
        Test your analytics configuration with a consent or bank statement PDF. Credentials are auto-configured from your saved setup.
      </div>

      {/* Top-level tabs */}
      <div className="equal-ds-root">
        <TabSwitcher
          tabs={[
            { id: 'consent', label: 'From Consent' },
            { id: 'pdf', label: 'From Bank Statement PDF' },
          ]}
          activeTab={activeTab}
          onTabChange={(t: string) => setActiveTab(t as 'consent' | 'pdf')}
          variant="horizontal"
        />
      </div>

      <div style={{ height: 28 }} />

      {/* ═══ CONSENT TAB ═══ */}
      {activeTab === 'consent' && (
        <div>
          {/* Format selector always visible */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden', backgroundColor: '#fff', marginBottom: consentJourney ? 0 : 0 }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9', backgroundColor: '#f8fafc' }}>
              <div style={{ fontSize: 15, color: '#0f172a' }}><b>Run Analytics on a Consent</b></div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Invokes Analytics JSON or Excel API.</div>
            </div>

            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}><b>Select API</b></div>
                <div className="equal-ds-root">
                  <TabSwitcher
                    tabs={[{ id: 'json', label: 'JSON API' }, { id: 'excel', label: 'Excel API' }]}
                    activeTab={consentFormat}
                    onTabChange={(t: string) => setConsentFormat(t as 'json' | 'excel')}
                    variant="horizontal"
                  />
                </div>
              </div>

              {/* No journeys of this format configured → empty state; journeys exist → full form (selector handles active/inactive) */}
              {activeConsentJourneys.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 20px' }}>
                  <AlertCircle style={{ width: 36, height: 36, color: '#94a3b8', margin: '0 auto 12px' }} />
                  <div style={{ fontSize: 14, color: '#0f172a', marginBottom: 6 }}>
                    <b>No {consentFormat === 'excel' ? 'Excel' : 'JSON'} analytics journey configured</b>
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
                    Configure an analytics journey to test {consentFormat === 'excel' ? 'Excel' : 'JSON'} analytics with a consent ID.
                  </div>
                  <div className="equal-ds-root">
                    <Button variant="primary" size="md" onClick={onConfigure}>Configure Analytics</Button>
                  </div>
                </div>
              ) : (
                <>
                  <ActiveJourneySelector
                    label={consentFormat === 'excel' ? 'Excel Analytics' : 'JSON Analytics'}
                    activeJourneyId={consentJourney}
                    availableJourneys={activeConsentJourneys}
                    activatingJourney={activatingJourney}
                    showPicker={showConsentPicker}
                    onTogglePicker={() => setShowConsentPicker(v => !v)}
                    onActivate={id => { activateJourney(id, consentActiveType); setShowConsentPicker(false); }}
                    onConfigure={onConfigure}
                  />

                  <div className="equal-ds-root">
                    <InputField
                      label="Consent ID"
                      value={consentId}
                      onChange={e => setConsentId(e.target.value)}
                      placeholder="e.g., d3d75e08-1d3e-401c-9d2a-a3cee6cf2b30"
                      required
                    />
                  </div>

                  <div style={{ height: 28 }} />

                  <div className="equal-ds-root">
                    <Button variant="primary" size="md" onClick={runConsent} disabled={!consentId.trim() || !consentJourney || consentRunning}>
                      {consentRunning ? <><Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> Generating...</> : <><Play style={{ width: 14, height: 14 }} /> Generate Analytics</>}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Curl panel — only when journey is active */}
          {consentJourney && (
            <div style={{ marginTop: 24 }}>
              <CurlPanel
                title={`Analytics ${consentFormat === 'json' ? 'JSON' : 'Excel'}`}
                curl={consentCurl}
                response={consentResponse}
                error={consentError}
              />
            </div>
          )}
        </div>
      )}

      {/* ═══ PDF TAB ═══ */}
      {activeTab === 'pdf' && (
        <div>
          {/* Card: always shown with format selector */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden', backgroundColor: '#fff', marginBottom: pdfJourney ? 0 : 0 }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9', backgroundColor: '#f8fafc' }}>
              <div style={{ fontSize: 15, color: '#0f172a' }}><b>Run Analytics on a Bank Statement PDF</b></div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Upload a PDF, push it, then pull the result.</div>
            </div>

            <div style={{ padding: '24px' }}>
              {/* Format selector — always visible */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}><b>Select API</b></div>
                <div className="equal-ds-root">
                  <TabSwitcher
                    tabs={[{ id: 'json', label: 'JSON API' }, { id: 'excel', label: 'Excel API' }]}
                    activeTab={pdfFormat}
                    onTabChange={(t: string) => { setPdfFormat(t as 'json' | 'excel'); setPdfRefToken(''); setPdfPushResponse(null); setPdfPullResponse(null); }}
                    variant="horizontal"
                  />
                </div>
              </div>

              {/* No journeys of this format configured → empty state; journeys exist → full form (selector handles active/inactive) */}
              {activePdfJourneys.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 20px' }}>
                  <AlertCircle style={{ width: 36, height: 36, color: '#94a3b8', margin: '0 auto 12px' }} />
                  <div style={{ fontSize: 14, color: '#0f172a', marginBottom: 6 }}>
                    <b>No {pdfFormat === 'excel' ? 'Excel' : 'JSON'} PDF analytics journey configured</b>
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
                    Configure a journey with PDF as input to test {pdfFormat === 'excel' ? 'Excel' : 'JSON'} PDF analytics.
                  </div>
                  <div className="equal-ds-root">
                    <Button variant="primary" size="md" onClick={onConfigure}>Configure Analytics</Button>
                  </div>
                </div>
              ) : (
                <>
                  <ActiveJourneySelector
                    label={pdfFormat === 'excel' ? 'PDF Excel Analytics' : 'PDF JSON Analytics'}
                    activeJourneyId={pdfJourney}
                    availableJourneys={activePdfJourneys}
                    activatingJourney={activatingJourney}
                    showPicker={showPdfPicker}
                    onTogglePicker={() => setShowPdfPicker(v => !v)}
                    onActivate={id => { activateJourney(id, pdfActiveType); setShowPdfPicker(false); }}
                    onConfigure={onConfigure}
                  />

                  {/* File picker */}
                  <div className="pdf-upload-box" style={{ marginBottom: 20 }}>
                    {pdfFileName ? (
                      <div>
                        <div style={{ display: 'inline-flex', width: 48, height: 48, borderRadius: '50%', backgroundColor: '#dcfce7', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                          <FileText style={{ width: 22, height: 22, color: '#16a34a' }} />
                        </div>
                        <div style={{ fontSize: 14, color: '#0f172a' }}><b>{pdfFileName}</b></div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Ready to push</div>
                        <button onClick={() => { setPdfFileName(''); setPdfFileBase64(''); setPdfRefToken(''); setPdfPushResponse(null); setPdfPullResponse(null); }}
                          style={{ marginTop: 10, fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>Remove file</button>
                      </div>
                    ) : (
                      <label style={{ cursor: 'pointer', display: 'block' }}>
                        <div style={{ display: 'inline-flex', width: 48, height: 48, borderRadius: '50%', backgroundColor: '#dbeafe', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                          <Upload style={{ width: 22, height: 22, color: '#2563eb' }} />
                        </div>
                        <div style={{ fontSize: 14, color: '#0f172a' }}><b>Click to select bank statement PDF</b></div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>PDF files only</div>
                        <input type="file" accept=".pdf" onChange={handlePdfFileSelect} style={{ display: 'none' }} />
                      </label>
                    )}
                  </div>

                  {/* ref_token display */}
                  {pdfRefToken && (
                    <div style={{ padding: '14px 16px', borderRadius: 10, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}><b>ref_token</b></div>
                      <div style={{ fontSize: 13, color: '#166534', wordBreak: 'break-all', fontFamily: "'SF Mono', monospace" }}>{pdfRefToken}</div>
                    </div>
                  )}

                  {/* Push + Pull side by side */}
                  <div className="equal-ds-root" style={{ display: 'flex', gap: 12 }}>
                    <Button variant="primary" size="md" onClick={runPdfPush} disabled={!pdfFileBase64 || !pdfJourney || pdfPushRunning}>
                      {pdfPushRunning ? <><Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> Pushing...</> : <><Play style={{ width: 14, height: 14 }} /> Push PDF</>}
                    </Button>
                    <Button variant="secondary" size="md" onClick={runPdfPull} disabled={!pdfRefToken || pdfPullRunning}>
                      {pdfPullRunning ? <><Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> Pulling...</> : <><Play style={{ width: 14, height: 14 }} /> Pull Result</>}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Curl panels — only when journey is active */}
          {pdfJourney && (
            <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <CurlPanel
                title={`PDF Push (${pdfFormat.toUpperCase()})`}
                curl={pdfPushCurl}
                response={pdfPushResponse}
                error={pdfPushError}
              />
              <CurlPanel
                title={`PDF Pull ${pdfFormat === 'excel' ? 'Excel' : 'JSON'}`}
                curl={pdfPullCurl}
                response={pdfPullResponse}
                error={pdfPullError}
              />
            </div>
          )}
        </div>
      )}

      <DataModal open={pdfViewOpen} data={pdfData} onClose={() => setPdfViewOpen(false)} />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .pdf-upload-box {
          border: 1.5px dashed #cbd5e1 !important;
          border-radius: 12px !important;
          padding: 36px 20px !important;
          text-align: center !important;
          margin-bottom: 20px !important;
          background-color: #f8fafc !important;
        }
        .curl-scroll::-webkit-scrollbar { width: 0; height: 0; }
        .curl-scroll { scrollbar-width: none; -ms-overflow-style: none; }
        #test-journey-select {
          width: 100% !important;
          max-width: 480px !important;
          padding: 12px 44px 12px 16px !important;
          font-size: 14px !important;
          color: #0f172a !important;
          border: 1.5px solid #d1d5db !important;
          border-radius: 10px !important;
          background-color: #fff !important;
          outline: none !important;
          height: 46px !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E") !important;
          background-repeat: no-repeat !important;
          background-position: right 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
        }
        .try-it-btn {
          padding: 8px 28px !important;
          font-size: 14px !important;
          color: #fff !important;
          border: none !important;
          border-radius: 8px !important;
          background-color: #22c55e !important;
          cursor: pointer !important;
        }
        .try-it-btn:disabled { cursor: default !important; }
      `}</style>
    </div>
  );
};
